import {
  Amenity as AmenityType,
  FacilityDetails,
  Gender,
  ProgramId,
  ProgramLevel,
  TagType,
  TimetableId,
} from '@gyym/sdk'
import {
  AutoLink,
  AwardBadge,
  Badge,
  Button,
  CalendarDateSelect,
  Carousel,
  CheckboxField,
  cn,
  Empty,
  FacilityInfo,
  FacilityInfoProps,
  Link,
  Loading,
  ProgramCard,
  Rating,
  ReadMore,
  ReviewBlock,
  ReviewList,
  SwiperStyles,
  TimetableList,
  Typography,
  useSnackbar,
} from '@gyym/ui'
import {
  Activity,
  Amenity,
  Clock,
  Heart,
  Link as LinkIcon,
  LocationOutline,
  Point,
  Tel,
} from '@gyym/ui/icons'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import React from 'react'

import Map from '@/components/UI/Map'
import { useAuthUser } from '@/modules/Auth'
import { getFacilityAward, toAwardBadgeCategories } from '@/modules/Awards'
import {
  buildFacilityLink,
  formatCityCode,
  useCanRequestReservation,
  useFacilityTimetables,
  useTimetableReserve,
} from '@/modules/Facilities'
import { searchQueryConverter } from '@/modules/FacilitySearch'
import { useToggleFavorite } from '@/modules/Favorites'
import { buildProgramLink } from '@/modules/Programs'
import { genderLabels } from '@/modules/Users'
import { formatNumber } from '@/utils/formatNumber'
import { getImageUrl } from '@/utils/getImageUrl'
import { nl2br } from '@/utils/nl2br'

import {
  AlertBox,
  BleedContent,
  BulletedList,
  BulletedListItem,
  Container,
  StyledArrow,
  TopContent,
} from './styles'

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return ''
  }
}

const getMapQuery = (facility: FacilityDetails): string => {
  // Use latitude and longitude if available
  if (facility.latitude && facility.longitude) {
    return facility.latitude + ',' + facility.longitude
  }
  // Use address if available
  // The building name is expected to be separated by spaces after the address
  return facility.address.split(' ')[0] as string
}

const getMapLink = (facility: FacilityDetails): string =>
  `https://maps.google.co.jp/maps?z=16&q=${getMapQuery(facility)}`

const amenities: { key: keyof AmenityType; label: string }[] = [
  {
    key: 'locker',
    label: 'ロッカー',
  },
  {
    key: 'shower',
    label: 'シャワー',
  },
  {
    key: 'powderRoom',
    label: '化粧室',
  },
  {
    key: 'dryer',
    label: 'ドライヤー',
  },
  {
    key: 'shampoo',
    label: 'シャンプー',
  },
  {
    key: 'lotion',
    label: '化粧水',
  },
  {
    key: 'spa',
    label: 'スパ',
  },
  {
    key: 'sauna',
    label: 'サウナ',
  },
  {
    key: 'coldBath',
    label: '水風呂',
  },
]

export interface FacilityDetailViewProps {
  facility: FacilityDetails
  prevPage?: {
    label: string
    href: string
  }
  prefecture: {
    name: string
    slug: string
  }
  city: {
    code: number
    name: string
  }
}

export const FacilityDetailView = ({
  facility,
  prevPage,
  prefecture,
  city,
}: FacilityDetailViewProps) => {
  // Cast to object type
  const scoreList = Array.isArray(facility.scoreList)
    ? {
        general: undefined,
        programContent: undefined,
        facilityCustomerService: undefined,
        facilityEquipment: undefined,
        facilityCleanliness: undefined,
      }
    : (facility.scoreList as {
        general: number
        programContent: number
        facilityCustomerService: number
        facilityEquipment: number
        facilityCleanliness: number
      })

  const router = useRouter()
  const { isLoggedIn, authUser } = useAuthUser()
  const { openSnackbar } = useSnackbar()
  const {
    data: timetables = [],
    mutate: mutateTimetables,
    isValidating: isTimetablesValidating,
  } = useFacilityTimetables(facility.id, { revalidateOnMount: true })
  const { loadingTimetableId, openReservationModal } = useTimetableReserve(
    facility,
    'facility',
    React.useCallback(
      async (timetableId: TimetableId) => {
        await mutateTimetables(
          timetables.map((t) =>
            t.id === timetableId
              ? {
                  ...t,
                  reserved: true,
                }
              : t,
          ),
          { revalidate: true },
        )
        return
      },
      [timetables, mutateTimetables],
    ),
    React.useCallback(
      (programId: ProgramId) =>
        facility.programs.find((p) => p.id === programId)?.image,
      [facility.programs],
    ),
  )

  const { data: { canRequest } = { canRequest: false } } =
    useCanRequestReservation({ revalidateOnMount: true })

  const favorite = useToggleFavorite()

  const [filteredTags, setFilteredTags] = React.useState<string[]>([])
  const hasStudioLesson = timetables.some((t) =>
    t.programTags.includes('studio_lesson'),
  )

  const timetablesWithProgram = timetables.map((t) => {
    const program = facility.programs.find((p) => p.id === t.programId)

    return {
      ...t,
      programId: program?.id ?? 0,
      programName: program?.name ?? '',
      programDuration: program?.duration ?? 0,
      programActivities: program?.activities ?? [],
      programLevel: program?.level ?? ProgramLevel.ENTRY,
      gender: program?.sexLimitation ?? Gender.NONE,
    }
  })

  // Group timetables by startAt
  const timetablesByStartAt = timetablesWithProgram.reduce((acc, timetable) => {
    const startAt = dayjs(timetable.startAt).format('YYYY-MM-DD')

    // Filter by tags
    if (
      filteredTags.length > 0 &&
      !filteredTags.every((tag) => timetable.programTags.includes(tag))
    ) {
      return acc
    }

    return {
      ...acc,
      [startAt]: [...(acc[startAt] ?? []), timetable],
    }
  }, {} as Record<string, typeof timetablesWithProgram>)

  // List of available dates
  const timetableStartAtDates = Object.keys(timetablesByStartAt)
  // First available date
  const firstTimetableDate = timetableStartAtDates[0]

  const [currentTimetableDate, setCurrentTimetableDate] =
    React.useState(firstTimetableDate)

  const currentTimetables = currentTimetableDate
    ? timetablesByStartAt[currentTimetableDate] ?? []
    : []

  // GYYM AWARDS 受賞情報 (受賞施設のみヘッダーに部門バッジを表示)
  const facilityAward = getFacilityAward(facility.id)

  const brandTag = facility.tags?.find((tag) => tag.type === TagType.BRAND)
  const brandLink = brandTag
    ? searchQueryConverter.toLink({ tags: [brandTag.value] }, [])
    : null

  const bottomBreadcrumbItems = [
    { label: 'TOP', href: '/' },
    ...(brandTag && brandLink
      ? [{ label: brandTag.label, href: brandLink.as }]
      : []),
    { label: prefecture.name, href: `/facilities/${prefecture.slug}` },
    {
      label: city.name,
      href: `/facilities/${prefecture.slug}/C${formatCityCode(city.code)}`,
    },
    { label: facility.name, href: '' },
  ]

  // Switch to the first available date when the timetable gets filtered
  React.useEffect(() => {
    setCurrentTimetableDate(firstTimetableDate)
  }, [firstTimetableDate])

  return (
    <>
      <AutoLink.GlobalStyles />

      <Container>
        <BleedContent>
          {/* ヘッダー (受賞バッジ・施設名・アクティビティ) はパンくず直下・画像スライダーの上に表示。
              背景は BleedContent の gray.06 に統一 */}
          <div className="px-4 py-6">
            {facilityAward && (
              <div className="flex flex-wrap gap-2">
                {toAwardBadgeCategories(facilityAward.categories).map(
                  (category) => (
                    <AwardBadge
                      key={category}
                      year={facilityAward.year}
                      category={category}
                    />
                  ),
                )}
              </div>
            )}

            {/* 受賞施設はバッジを含めて左寄せレイアウト、非受賞施設は従来の中央寄せを維持 */}
            <div
              className={cn('flex', facilityAward ? 'mt-1' : 'justify-center')}
            >
              <Typography variant="headline1" as="h1">
                {facility.name}
              </Typography>
            </div>

            <div
              className={cn(
                'mt-1 flex flex-wrap items-center gap-2',
                !facilityAward && 'justify-center',
              )}
            >
              <div className="flex items-center gap-2">
                <div className="flex">
                  <Activity />
                </div>

                <Typography variant="body1">
                  {facility.activities
                    .map((activity) => activity.name)
                    .join(' / ')}
                </Typography>
              </div>

              {facility.sexLimitation === Gender.MEN && (
                <Badge variant="men">男性専用</Badge>
              )}

              {facility.sexLimitation === Gender.WOMEN && (
                <Badge variant="women">女性専用</Badge>
              )}
            </div>
          </div>

          <Carousel
            infinite
            images={facility.images.map((image, i) => ({
              src: getImageUrl(image),
              alt: `${facility.name}の画像 ${i + 1}`,
              fill: true,
              sizes: '100%',
              style: { objectFit: 'cover' },
            }))}
          />

          <TopContent>
            <div className="mt-6 flex justify-between">
              <div className="flex flex-col">
                <Rating value={scoreList.general ?? 0} size="sm" />

                <div className="flex items-center">
                  <Typography variant="body2" color="gray.01">
                    評価平均{' '}
                    {typeof scoreList.general === 'number'
                      ? scoreList.general.toFixed(1)
                      : '--'}
                  </Typography>

                  {facility.reviewCount > 0 && (
                    <Typography variant="body2" color="gray.01" ml={1}>
                      ({facility.reviewCount}件)
                    </Typography>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <Button
                  variant="primary-outlined"
                  size="sm"
                  loading={favorite.isFavoriteLoading}
                  onClick={() => favorite.onToggle(facility.id)}
                >
                  <Heart
                    selected={favorite.isFavorited(facility.id)}
                    strokeColor="currentColor"
                  />{' '}
                  お気に入り
                </Button>
              </div>
            </div>

            {facility.importantNotice && (
              <div className="mt-4 flex">
                <AlertBox>{facility.importantNotice}</AlertBox>
              </div>
            )}

            <div className="mt-4 flex">
              <FacilityInfo
                items={
                  [
                    {
                      icon: <Point />,
                      text: `${formatNumber(facility.minTimetablePoint)}pt 〜`,
                    },
                    {
                      icon: <LocationOutline />,
                      text: facility.distance,
                    },
                    facility.businessHour && {
                      icon: <Clock />,
                      text: nl2br(facility.businessHour),
                    },
                    facility.tel && {
                      icon: <Tel />,
                      text: facility.tel,
                      link: {
                        href: `tel:${facility.tel}`,
                        external: true,
                      },
                    },
                    facility.homepage && {
                      icon: <LinkIcon />,
                      text: getHostname(facility.homepage),
                      link: {
                        href: facility.homepage,
                        external: true,
                        target: '_blank',
                      },
                    },
                  ].filter(Boolean) as FacilityInfoProps['items']
                }
              />
            </div>

            <div className="mt-4 flex flex-col">
              <ReadMore fixedButton>
                <AutoLink>{nl2br(facility.description)}</AutoLink>
              </ReadMore>
            </div>
          </TopContent>
        </BleedContent>

        <Typography id="amenities" variant="headline2" as="h2" mt={15}>
          設備・アメニティ
        </Typography>
        <Typography variant="caption1" as="div">
          Amenities
        </Typography>

        <div className="mt-6 flex flex-wrap gap-2 tablet:gap-3">
          {amenities.map(({ key, label }) => (
            <div
              key={key}
              className="flex w-[65px] flex-col items-center gap-1"
            >
              <Amenity type={key} active={facility.amenities[key]} />
              <Typography variant="caption1">{label}</Typography>
            </div>
          ))}
        </div>

        {facility.attention.length > 0 && (
          <>
            <Typography id="attention" variant="headline2" as="h2" mt={15}>
              注意事項
            </Typography>
            <Typography variant="caption1" as="div">
              Attention
            </Typography>

            <BulletedList>
              {facility.attention.map((attention, i) => (
                <BulletedListItem key={i}>
                  <Typography variant="body1" color="gray.01" as="span">
                    <AutoLink>{attention}</AutoLink>
                  </Typography>
                </BulletedListItem>
              ))}
            </BulletedList>
          </>
        )}

        <Typography id="review" variant="headline2" as="h2" mt={15}>
          施設レビュー
        </Typography>
        <Typography variant="caption1" as="div">
          Review
        </Typography>

        <div className="mt-6 flex flex-col gap-6">
          <ReviewBlock {...scoreList} count={facility.reviewCount} />

          {facility.reviews.length > 0 && (
            <ReviewList
              reviews={facility.reviews.slice(0, 2)}
              moreLink={
                facility.reviews.length > 2
                  ? {
                      href: `${buildFacilityLink(facility).as}/reviews`,
                      external: true,
                    }
                  : undefined
              }
            />
          )}

          {facility.reviews.length === 0 && (
            <Empty mt={6}>この施設にはまだレビューがありません</Empty>
          )}
        </div>

        <Typography id="access" variant="headline2" as="h2" mt={15}>
          アクセス
        </Typography>
        <Typography variant="caption1" as="div">
          Access
        </Typography>

        <div className="mt-6 flex flex-col gap-4">
          {facility.latitude && facility.longitude && (
            <div style={{ position: 'relative', height: 400, width: '100%' }}>
              <Map
                center={{
                  lat: facility.latitude,
                  lng: facility.longitude,
                }}
                locations={[
                  {
                    id: facility.id,
                    lat: facility.latitude,
                    lng: facility.longitude,
                  },
                ]}
                mapOptions={{
                  zoom: 16,
                  zoomControl: true,
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Typography variant="subhead1" as="h3">
              {facility.address}
            </Typography>
            <Typography variant="body2">{facility.distance}</Typography>
            <Typography variant="body2" as="p" wordBreak="break-all">
              <AutoLink>{nl2br(facility.access)}</AutoLink>
            </Typography>
            <div className="flex justify-end">
              <Link
                variant="secondary"
                href={getMapLink(facility)}
                external
                target="_blank"
                rel="noopener noreferrer"
              >
                マップを表示
              </Link>
            </div>
          </div>
        </div>

        <Typography id="program" variant="headline2" as="h2" mt={15}>
          実施プログラム
        </Typography>
        <Typography variant="caption1" as="div">
          Program
        </Typography>

        {facility.programs.length > 0 && (
          <div className="mt-6 flex flex-col gap-2">
            {facility.programs.map((program) => (
              <ProgramCard
                key={program.id}
                {...program}
                image={getImageUrl(program.image)}
                {...buildProgramLink(facility, program)}
              />
            ))}
          </div>
        )}

        {facility.programs.length === 0 && (
          <Empty mt={6}>現在ご利用いただけるプログラムがありません</Empty>
        )}

        <Typography id="schedule" variant="headline2" as="h2" mt={15}>
          スケジュール
        </Typography>
        <Typography variant="caption1" as="div">
          Schedule
        </Typography>

        {isTimetablesValidating && <Loading size="md" />}

        {!isTimetablesValidating && timetables.length === 0 && (
          <Empty mt={6}>現在ご利用いただけるスケジュールがありません</Empty>
        )}

        {!isTimetablesValidating && timetables.length > 0 && (
          <div className="mt-8 flex flex-col gap-3">
            {hasStudioLesson && (
              <div className="flex w-full items-center justify-center">
                <div className="flex">
                  <CheckboxField
                    name="showStudioLessonsOnly"
                    label="スタジオレッスンのみ表示"
                    checked={filteredTags.includes('studio_lesson')}
                    onChange={() => {
                      setFilteredTags((prev) => {
                        if (prev.includes('studio_lesson')) {
                          return prev.filter((tag) => tag !== 'studio_lesson')
                        }
                        return [...prev, 'studio_lesson']
                      })
                    }}
                  />
                </div>
              </div>
            )}

            <div className={`flex ${hasStudioLesson ? 'mt-5' : ''}`}>
              <SwiperStyles />
              <CalendarDateSelect
                id="timetable-dates"
                value={currentTimetableDate}
                options={timetableStartAtDates}
                onChange={setCurrentTimetableDate}
              />
            </div>

            <TimetableList
              timetables={currentTimetables}
              canRequestReservation={canRequest}
              loadingTimetableId={loadingTimetableId}
              onSelect={(timetable) => {
                if (
                  isLoggedIn &&
                  // 性別専用のタイムテーブルで、
                  // 性別が未設定のユーザー、または性別が異なるユーザーは予約できない
                  timetable.gender !== 'none' &&
                  (authUser?.sex === 'none' ||
                    authUser?.sex !== timetable.gender)
                ) {
                  openSnackbar({
                    message: '予約できません',
                    extra: `このプログラムは${
                      genderLabels[timetable.gender]
                    }専用のプログラムになり、${
                      genderLabels[timetable.gender]
                    }の方のみご予約できます。`,
                    variant: 'error',
                  })
                  return
                }

                if (timetable.isApprovable) {
                  const approvableUrl = `${
                    buildProgramLink(facility, {
                      id: timetable.programId,
                    }).as
                  }/reservation_request/${timetable.id}`

                  if (!isLoggedIn) {
                    openSnackbar({
                      message: '仮予約するにはログインが必要です',
                      action: {
                        label: 'ログインする',
                        href: `/login?path=${approvableUrl}`,
                      },
                    })
                    return
                  } else {
                    router.push(approvableUrl)
                  }
                } else {
                  openReservationModal(timetable.programId, timetable.id)
                }
              }}
            />

            <Typography
              variant="body2"
              color="gray.02"
              mt={1 /* + gap3 */}
              px={4}
            >
              ※Instr = インストラクターの略
            </Typography>
          </div>
        )}

        {facility.tags && facility.tags.length > 0 && (
          <div className="mt-[60px] flex flex-col gap-6">
            <Typography as="h2" variant="headline2" color="blue.01">
              関連キーワード
            </Typography>

            <div className="flex flex-wrap gap-2">
              {facility.tags.map((tag) => (
                <Link
                  key={tag.id}
                  variant="primary"
                  {...searchQueryConverter.toLink(
                    {
                      tags: [tag.value],
                    },
                    // Pass dummy prefectures since we only use the tags condition
                    [],
                  )}
                >
                  {tag.label}
                </Link>
              ))}
            </div>
          </div>
        )}

        {prevPage && (
          <div className="mt-[60px] flex justify-center">
            <Button className="w-[314px]" variant="primary-outlined" asChild>
              <Link href={prevPage?.href}>
                {prevPage.label}のフィットネスジム・スタジオ一覧をみる
              </Link>
            </Button>
          </div>
        )}

        {/* Bottom breadcrumb links */}
        <nav aria-label="パンくず" className="mt-[60px]">
          <ol
            itemScope
            itemType="https://schema.org/BreadcrumbList"
            className="m-0 flex list-none flex-wrap gap-1 p-0"
          >
            {bottomBreadcrumbItems.map((item, index) => (
              <li
                key={index}
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
                className="w-full pc:w-auto"
              >
                {index === 0 ? (
                  <Link variant="secondary" href={item.href} itemProp="item">
                    <span itemProp="name">{item.label}</span>
                  </Link>
                ) : index < bottomBreadcrumbItems.length - 1 ? (
                  <Typography
                    variant="body2"
                    color="gray.01"
                    as="span"
                    className="flex items-center gap-1"
                  >
                    <StyledArrow />
                    <Link variant="secondary" href={item.href} itemProp="item">
                      <span itemProp="name">{item.label}</span>
                    </Link>
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    color="gray.01"
                    as="span"
                    className="flex items-center gap-1"
                  >
                    <StyledArrow />
                    <span itemProp="name">{item.label}</span>
                  </Typography>
                )}
                <meta itemProp="position" content={`${index + 1}`} />
              </li>
            ))}
          </ol>
        </nav>
      </Container>
    </>
  )
}
